import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { Suggestion } from '../types';
import { useOrderSearch } from '../hooks';

interface SearchComponentProps {
  onSearch: (searchValue: string | number) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

const SearchComponent: React.FC<SearchComponentProps> = React.memo(({
  onSearch,
  placeholder = "Search...",
  className = "",
  style
}) => {
  const [value, setValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  
  const { suggestions, fetchSuggestions } = useOrderSearch();

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle input change and fetch suggestions
  const handleInputChange = useCallback(async (input: string) => {
    setValue(input);
    
    if (input.trim()) {
      const payload = {
        store_based: true,
        is_search: "true",
        product_name: input,
      };

      try {
        const fetchedSuggestions = await fetchSuggestions(payload);
        setShowSuggestions(fetchedSuggestions.length > 0);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  }, [fetchSuggestions]);

  // Handle suggestion click
  const handleSuggestionClick = useCallback((suggestion: Suggestion) => {
    setValue(suggestion.name);
    setShowSuggestions(false);
    onSearch(suggestion.id);
  }, [onSearch]);

  // Handle search button click or Enter key
  const handleSearch = useCallback(() => {
    onSearch(value);
    setShowSuggestions(false);
  }, [value, onSearch]);

  // Handle Enter key press
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  return (
    <div className={`search-container ${className}`} style={style} ref={dropdownRef}>
      {/* <div className="search-input-wrapper"> */}
        <input
          className="mr-sm-2 search-input order-input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <Button
          variant="outline-primary search-btn order-search-btn"
          onClick={handleSearch}
        >
          <FaSearch />
        </Button>
      {/* </div> */}

      {showSuggestions && suggestions.length > 0 && (
        <div className="suggestions-dropdown">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.id}
              className="suggestion-item"
              onClick={() => handleSuggestionClick(suggestion)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSuggestionClick(suggestion);
                }
              }}
            >
              {suggestion.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

SearchComponent.displayName = 'SearchComponent';

export default SearchComponent;